import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Award, Clock, Users, Lock, Unlock, Play, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import BottomNavigation from '../components/BottomNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentQuiz() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchQuizData();
  }, []);

  const fetchQuizData = async () => {
    try {
      const [quizzesRes, resultsRes] = await Promise.all([
        axios.get(`${API}/tests`, getAuthHeader()),
        axios.get(`${API}/tests/results`, getAuthHeader())
      ]);

      setQuizzes(quizzesRes.data);
      setMyResults(resultsRes.data);
    } catch (error) {
      toast.error('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = (quizId) => {
    navigate(`/test/${quizId}`);
  };

  const getQuizStatus = (quizId) => {
    const result = myResults.find(r => r.test_id === quizId);
    return result ? { completed: true, score: result.score, total: result.total_marks } : { completed: false };
  };

  // Separate free and paid quizzes (for now, all quizzes are free - this is for future paid quiz feature)
  const freeQuizzes = quizzes.filter(q => !q.is_paid);
  const paidQuizzes = quizzes.filter(q => q.is_paid);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 pb-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-teal-500 text-white sticky top-0 z-40">
        <div className="px-4 py-6">
          <div className="flex items-center space-x-3 mb-2">
            <Award className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Quiz & Test Series</h1>
          </div>
          <p className="text-white/80 text-sm">Practice with free and premium quizzes</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Award className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{quizzes.length}</p>
                <p className="text-xs text-slate-500">Total Quizzes</p>
              </div>
            </div>
          </Card>
          <Card className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{myResults.length}</p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="w-full bg-white border border-slate-200 p-1 rounded-xl">
            <TabsTrigger 
              value="all" 
              className="flex-1 rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
            >
              All Quizzes
            </TabsTrigger>
            <TabsTrigger 
              value="free" 
              className="flex-1 rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
            >
              Free
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              className="flex-1 rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
            >
              Completed
            </TabsTrigger>
          </TabsList>

          {/* All Quizzes */}
          <TabsContent value="all" className="mt-4">
            {quizzes.length === 0 ? (
              <Card className="bg-white rounded-xl p-8 text-center shadow-sm">
                <Award className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No quizzes available yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {quizzes.map((quiz) => {
                  const status = getQuizStatus(quiz.id);
                  return (
                    <Card
                      key={quiz.id}
                      data-testid={`quiz-card-${quiz.id}`}
                      className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="font-bold text-slate-900">{quiz.title}</h3>
                              {quiz.is_paid ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 flex items-center">
                                  <Lock className="w-3 h-3 mr-1" />
                                  Premium
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-700 flex items-center">
                                  <Unlock className="w-3 h-3 mr-1" />
                                  Free
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 line-clamp-2">{quiz.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                          <div className="flex items-center space-x-4">
                            <span className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {quiz.duration_minutes} min
                            </span>
                            <span className="flex items-center">
                              <Award className="w-4 h-4 mr-1" />
                              {quiz.total_marks} marks
                            </span>
                            <span className="flex items-center">
                              <BookOpen className="w-4 h-4 mr-1" />
                              {quiz.questions?.length || 0} Qs
                            </span>
                          </div>
                        </div>

                        {status.completed ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="w-5 h-5 text-teal-500" />
                              <span className="text-sm font-medium text-teal-700">
                                Score: {status.score}/{status.total}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStartQuiz(quiz.id)}
                              className="rounded-full"
                            >
                              Retake
                            </Button>
                          </div>
                        ) : (
                          <Button
                            data-testid={`start-quiz-${quiz.id}`}
                            onClick={() => handleStartQuiz(quiz.id)}
                            className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Start Quiz
                          </Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Free Quizzes */}
          <TabsContent value="free" className="mt-4">
            {quizzes.filter(q => !q.is_paid).length === 0 ? (
              <Card className="bg-white rounded-xl p-8 text-center shadow-sm">
                <Unlock className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No free quizzes available</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {quizzes.filter(q => !q.is_paid).map((quiz) => {
                  const status = getQuizStatus(quiz.id);
                  return (
                    <Card
                      key={quiz.id}
                      className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="font-bold text-slate-900">{quiz.title}</h3>
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-700">
                                Free
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 line-clamp-2">{quiz.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-slate-500 mb-4">
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {quiz.duration_minutes} min
                          </span>
                          <span className="flex items-center">
                            <Award className="w-4 h-4 mr-1" />
                            {quiz.total_marks} marks
                          </span>
                        </div>

                        <Button
                          onClick={() => handleStartQuiz(quiz.id)}
                          className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {status.completed ? 'Retake Quiz' : 'Start Quiz'}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Completed Quizzes */}
          <TabsContent value="completed" className="mt-4">
            {myResults.length === 0 ? (
              <Card className="bg-white rounded-xl p-8 text-center shadow-sm">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No completed quizzes yet</p>
                <Button
                  onClick={() => setActiveTab('all')}
                  className="mt-4 rounded-full"
                >
                  Browse Quizzes
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {myResults.map((result) => {
                  const quiz = quizzes.find(q => q.id === result.test_id);
                  if (!quiz) return null;
                  const percentage = Math.round((result.score / result.total_marks) * 100);
                  return (
                    <Card
                      key={result.id}
                      className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900">{quiz.title}</h3>
                            <p className="text-xs text-slate-500 mt-1">
                              Completed on {new Date(result.submitted_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                            percentage >= 70 ? 'bg-teal-100 text-teal-700' :
                            percentage >= 40 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {percentage}%
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-slate-600">
                            Score: <span className="font-semibold">{result.score}/{result.total_marks}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStartQuiz(quiz.id)}
                            className="rounded-full"
                          >
                            Retake
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <BottomNavigation />
    </div>
  );
}
